/**
 * Marketing Ops Dashboard Service
 * API calls for the marketing pipeline CRM
 */
import api from './index';

const marketingService = {
  /**
   * Get pipeline data (all visitors grouped by stage)
   * @param {object} params Query params (days, stage, country, source)
   * @returns {Promise<object>} { pipeline, summary, totalCount }
   */
  async getPipeline(params = {}) {
    const query = new URLSearchParams();
    if (params.days) query.append('days', params.days);
    if (params.maxStep) query.append('maxStep', params.maxStep);
    if (params.source) query.append('source', params.source);
    if (params.quietDays) query.append('quietDays', params.quietDays);
    if (params.activeWithinDays) query.append('activeWithinDays', params.activeWithinDays);
    if (params.startDate) query.append('startDate', params.startDate);
    if (params.endDate) query.append('endDate', params.endDate);
    const qs = query.toString();
    const response = await api.get(`/marketing/pipeline${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  /**
   * Get visitor detail (full profile + timeline)
   * @param {string} visitorId Application ID
   * @returns {Promise<object>} Full visitor detail
   */
  async getVisitorDetail(visitorId) {
    const response = await api.get(`/marketing/visitors/${visitorId}`);
    return response.data;
  },

  /**
   * Get visitor feedback (checkout prompt + email one-tap answers)
   * @param {object} params Query params (days)
   * @returns {Promise<object>} { entries, summary }
   */
  async getFeedback(params = {}) {
    const query = new URLSearchParams();
    if (params.days) query.append('days', params.days);
    const qs = query.toString();
    const response = await api.get(`/marketing/feedback${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  /**
   * Search people by name, email, phone, trademark or business
   * @param {string} q Search term (min 2 chars)
   * @returns {Promise<object>} { results }
   */
  async searchPeople(q) {
    const response = await api.get(`/marketing/search?q=${encodeURIComponent(q)}`);
    return response.data;
  },

  /**
   * TM Watch: register presence for every watched trade mark
   * @returns {Promise<object>} { configured, entries, summary }
   */
  async getTmWatch() {
    const response = await api.get('/marketing/tmwatch');
    return response.data;
  },

  /**
   * Ask the AI reporting assistant (read-only over marketing data)
   * @param {Array<{role: string, content: string}>} messages Chat history ending with the user question
   * @returns {Promise<object>} { reply }
   */
  async askAssistant(messages) {
    const response = await api.post('/marketing/assistant', { messages });
    return response.data;
  },

  /**
   * Send the feedback-ask email to many visitors at once.
   * The backend skips DNC/paid, anyone who already gave feedback, and
   * anyone asked within the last 30 days.
   * @param {string[]} visitorIds
   * @returns {Promise<object>} { sent, skipped, failed, summary }
   */
  async bulkFeedback(visitorIds) {
    const response = await api.post('/marketing/actions/bulk-feedback', { visitorIds });
    return response.data;
  },

  /**
   * Add a note to a visitor
   * @param {string} visitorId Application ID
   * @param {string} content Note content
   * @returns {Promise<object>} Created note
   */
  async addNote(visitorId, content) {
    const response = await api.post('/marketing/actions/add-note', {
      visitorId,
      content,
    });
    return response.data;
  },

  /**
   * Send follow-up email to a visitor.
   * @param {string} visitorId Visitor tracking ID
   * @param {object} compose Optional composer fields:
   *   { subject, bodyText, includeBlocks, templateOverride, customMessage }
   * @returns {Promise<object>} Result
   */
  async sendFollowUp(visitorId, compose = {}) {
    const response = await api.post('/marketing/actions/send-follow-up', {
      visitorId,
      ...compose,
    });
    return response.data;
  },

  /**
   * Update visitor follow-up status
   * @param {string} visitorId Application ID
   * @param {string} status New status
   * @returns {Promise<object>} Result
   */
  async updateStatus(visitorId, status) {
    const response = await api.post('/marketing/actions/update-status', {
      visitorId,
      status,
    });
    return response.data;
  },

  /**
   * Preview outreach email — returns rendered HTML, the editable draft
   * (subject + bodyText + blocks) and sender info, without sending.
   * @param {string} visitorId Visitor tracking ID
   * @param {object} compose Optional composer fields:
   *   { subject, bodyText, includeBlocks, templateOverride, customMessage }
   * @returns {Promise<object>} { templateName, subject, draft, sender, html, ... }
   */
  async previewEmail(visitorId, compose = {}) {
    const response = await api.post('/marketing/actions/preview-email', {
      visitorId,
      ...compose,
    });
    return response.data;
  },

  /**
   * Set (or clear) a follow-up reminder for a visitor.
   * @param {string} visitorId Visitor tracking ID
   * @param {number} days Days from now (0/null clears the reminder)
   */
  async setReminder(visitorId, days) {
    const response = await api.post('/marketing/actions/set-reminder', {
      visitorId,
      days,
    });
    return response.data;
  },

  /**
   * List generated reports (the 24-hour SLA queue)
   * @param {object} params Optional { status }
   * @returns {Promise<object>} { reports, pendingCount }
   */
  async getReports(params = {}) {
    const query = new URLSearchParams();
    if (params.status) query.append('status', params.status);
    const qs = query.toString();
    const response = await api.get(`/marketing/reports${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  /**
   * Get a single report (full sections + register search + quote)
   * @param {string} reportId
   * @returns {Promise<object>} { report, application }
   */
  async getReport(reportId) {
    const response = await api.get(`/marketing/reports/${reportId}`);
    return response.data;
  },

  /**
   * Re-run the report generator for a report
   * @param {string} reportId
   */
  async regenerateReport(reportId) {
    const response = await api.post(`/marketing/reports/${reportId}/regenerate`);
    return response.data;
  },

  /**
   * Save edits to a report (sections and/or email subject/body overrides)
   * @param {string} reportId
   * @param {object} updates { sections?, emailSubject?, emailBodyText? }
   */
  async updateReport(reportId, updates) {
    const response = await api.post(`/marketing/reports/${reportId}/update`, updates);
    return response.data;
  },

  /**
   * Edit the quote — writes back to the pricing record (quoteStatus: confirmed).
   * Editing a quote whose report has already been sent creates a revision.
   * @param {string} reportId
   * @param {object} quote { countries?, classes?, autoPrice?, serviceFeeTotal?,
   *                         govFeeTotal?, discount?, total?, currency? }
   */
  async updateReportQuote(reportId, quote) {
    const response = await api.post(`/marketing/reports/${reportId}/update-quote`, quote);
    return response.data;
  },

  /**
   * Price a prospective quote scope without saving it (live total in the editor)
   * @param {string} reportId
   * @param {object} scope { countries?, classCount?, currency? }
   * @returns {Promise<object>} { quote }
   */
  async priceReportQuote(reportId, scope) {
    const response = await api.post(`/marketing/reports/${reportId}/price-quote`, scope);
    return response.data;
  },

  /**
   * Send the report + quote email with a fresh dashboard magic link
   * @param {string} reportId
   * @param {object} compose Optional { subject, bodyText }
   */
  async sendReport(reportId, compose = {}) {
    const response = await api.post('/marketing/actions/send-report', {
      reportId,
      ...compose,
    });
    return response.data;
  },

  /**
   * Send a revised quote to a client who already has their report
   * @param {string} reportId
   * @param {object} compose Optional { subject, bodyText }
   */
  async sendQuoteRevision(reportId, compose = {}) {
    const response = await api.post('/marketing/actions/send-quote-revision', {
      reportId,
      ...compose,
    });
    return response.data;
  },

  /**
   * The visitor's uploaded logo as a blob. Fetched through the API rather than
   * linked directly: the stored URL's SAS token expires, and an <img> tag
   * can't send our auth header.
   * @param {string} applicationId
   * @returns {Promise<Blob>}
   */
  async downloadTrademarkImage(applicationId) {
    const response = await api.get(
      `/marketing/trademark-image?applicationId=${encodeURIComponent(applicationId)}`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  /**
   * Country/pricing reference table (public endpoint) — the authoritative
   * country list for the quote editor's country picker
   */
  async getPricingConfig() {
    const response = await api.get('/pricing-config');
    return response.data;
  },

  /**
   * Class reference for the quote class picker. Terms come from the IP
   * Australia picklist, so anything picked keeps the $250/class filing fee.
   * @param {object} params Optional { classNumber, q, limit }
   */
  async getClassTerms(params = {}) {
    const query = new URLSearchParams();
    if (params.classNumber) query.append('class', params.classNumber);
    if (params.q) query.append('q', params.q);
    if (params.limit) query.append('limit', params.limit);
    const qs = query.toString();
    const response = await api.get(`/marketing/class-terms${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  /**
   * AI-suggested goods/services for a class, snapped to the picklist
   * @param {string} reportId
   * @param {number} classNumber
   */
  async suggestGoodsServices(reportId, classNumber) {
    const response = await api.post(`/marketing/reports/${reportId}/suggest-goods`, { classNumber });
    return response.data;
  },

  /**
   * Propose picklist replacements for wording that isn't on the picklist.
   * Returns proposals only — nothing is saved until the quote is saved.
   * @param {string} reportId
   * @param {number} [classNumber] omit to cover every affected class
   */
  async fixPicklistTerms(reportId, classNumber) {
    const response = await api.post(`/marketing/reports/${reportId}/fix-terms`,
      classNumber != null ? { classNumber } : {});
    return response.data;
  },

  /**
   * Download the printable call sheet PDF (one A4 page per Call-list lead,
   * capped at 50 server-side).
   * @returns {Promise<Blob>} PDF blob
   */
  async downloadCallSheet() {
    const response = await api.get('/marketing/call-sheet', { responseType: 'blob' });
    return response.data;
  },

  async updateContactStatus(visitorId, contactStatus) {
    const response = await api.post('/marketing/actions/update-status', {
      visitorId,
      status: contactStatus,
    });
    return response.data;
  },
};

export default marketingService;
